import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface InstallFormData {
    // Basic Configuration
    domain: string;
    dbHost: string;
    dbDatabase: string;
    dbUser: string;
    dbPassword: string;
    defaultLanguage: string;

    // Authentication Configuration
    authCodeEnabled: boolean;
    oidcEnabled: boolean;
    adminAuthCode: string;

    // OIDC Configuration
    oidcDiscoveryUrl: string;
    oidcClientId: string;
    oidcClientSecret: string;
    oidcRequiredGroup: string;
    oidcAdminGroup: string;

    // Mail Configuration
    mailEnabled: boolean;
    mailHost: string;
    mailPort: string;
    mailUser: string;
    mailPassword: string;
    mailSender: string;
    mailReplyTo: string;

    // Multiple Events Configuration
    events: Array<{
        name: string;
        location: string;
        startTime: string;
        endTime: string;
        recurrenceType: 'weekly' | 'monthly';
        weekdays: number[];
        monthWeek: number;
        monthDay: number;
    }>;

    // ChurchTools Integration
    churchToolsEnabled: boolean;
    churchToolsUrl: string;
    churchToolsToken: string;
    churchToolsCategoriesEnabled: boolean;
    churchToolsAgendaEnabled: boolean;
    churchToolsFilesEnabled: boolean;

    // Schedule Feature
    scheduleEnabled: boolean;
    scheduleMinDuration: string;
    scheduleDefaultStartTime: string;
    scheduleDefaultSetupTime: string;
    scheduleDefaultTeardownTime: string;
}

export interface StepErrors {
    [key: number]: string[];
}

interface InstallState {
    formData: InstallFormData;
    activeStep: number;
    visitedSteps: number[];
    stepErrors: StepErrors;
    submitting: boolean;
}

const initialState: InstallState = {
    formData: {
        domain: typeof window !== 'undefined' ? window.location.hostname : '',
        dbHost: 'localhost',
        dbDatabase: '',
        dbUser: '',
        dbPassword: '',
        defaultLanguage: 'en',
        authCodeEnabled: true,
        oidcEnabled: false,
        adminAuthCode: '',
        oidcDiscoveryUrl: '',
        oidcClientId: '',
        oidcClientSecret: '',
        oidcRequiredGroup: '',
        oidcAdminGroup: '',
        mailEnabled: false,
        mailHost: '',
        mailPort: '993',
        mailUser: '',
        mailPassword: '',
        mailSender: '',
        mailReplyTo: '',
        events: [
            {
                name: 'Service',
                location: 'Church',
                startTime: '09:30:00',
                endTime: '12:00:00',
                recurrenceType: 'weekly',
                weekdays: [7],
                monthWeek: 1,
                monthDay: 7,
            },
        ],
        churchToolsEnabled: false,
        churchToolsUrl: '',
        churchToolsToken: '',
        churchToolsCategoriesEnabled: true,
        churchToolsAgendaEnabled: true,
        churchToolsFilesEnabled: true,
        scheduleEnabled: true,
        scheduleMinDuration: '60',
        scheduleDefaultStartTime: '10:00',
        scheduleDefaultSetupTime: '30',
        scheduleDefaultTeardownTime: '15',
    },
    activeStep: 0,
    visitedSteps: [0],
    stepErrors: {},
    submitting: false,
};

const installSlice = createSlice({
    name: 'install',
    initialState,
    reducers: {
        updateFormData: (state, action: PayloadAction<Partial<InstallFormData>>) => {
            state.formData = { ...state.formData, ...action.payload };
        },
        setActiveStep: (state, action: PayloadAction<number>) => {
            state.activeStep = action.payload;
            if (!state.visitedSteps.includes(action.payload)) {
                state.visitedSteps.push(action.payload);
            }
        },
        setStepErrors: (state, action: PayloadAction<{ step: number; errors: string[] }>) => {
            if (action.payload.errors.length === 0) {
                delete state.stepErrors[action.payload.step];
            } else {
                state.stepErrors[action.payload.step] = action.payload.errors;
            }
        },
        clearStepErrors: (state, action: PayloadAction<number>) => {
            delete state.stepErrors[action.payload];
        },
        setSubmitting: (state, action: PayloadAction<boolean>) => {
            state.submitting = action.payload;
        },
    },
});

export const { updateFormData, setActiveStep, setStepErrors, clearStepErrors, setSubmitting } =
    installSlice.actions;

export default installSlice.reducer;
